/**
 * Dev-only **Feature toggles** — force a web feature flag On or Off in THIS browser, the web port of the
 * native apps' Dev Tools ▸ Feature Toggles screen (`ios/.../FeatureTogglesScreen.swift`,
 * `android/.../FeatureToggles.kt`).
 *
 * Same model as native, deliberately:
 *
 *   - Every toggle the app conducts is listed (`WEB_TOGGLES`), with its **natural** conducted state
 *     shown on the Default pill ("Default · On" / "Default · Off").
 *   - Per toggle a **tri-state**: Default (no override) · On (`"true"`) · Off (`"false"`).
 *   - A **Persist** switch (default ON) deciding whether the override survives a reload — native's
 *     "survives a relaunch". Persisted overrides go to `localStorage`; session-only ones to
 *     `sessionStorage`, which dies with the tab exactly as native's in-memory override dies with the
 *     process.
 *
 * The flag read-path consults `toggleOverride()` FIRST (see `lib/instance-auth-flag.ts`), so a forced
 * value behaves like the real conducted one everywhere downstream — no special-casing per call site.
 *
 * Every entry point is gated on `devToolsUnlocked()`, the same gate as the mock layer: a real user's
 * browser holds no grant, so a key left in storage (or hand-written) does nothing at all.
 */

import { devToolsUnlocked } from './dev-tools';

/** A forced value. Kept as the string native uses, so the two stores read identically. */
export type ToggleOverride = 'true' | 'false';

export interface WebToggle {
  /** The Petri/Conductor spec id — what the server conducts. */
  spec: string;
  /** What it gates, in one line, for the panel. */
  description: string;
}

/** The signed-instance auth gate. Exported so the flag's own module reads the same literal. */
export const INSTANCE_AUTH_SPEC = 'symphonyInstanceAuthWeb';

/** The live voice-call gate. Exported so the flag's own module reads the same literal. */
export const VOICE_CALL_SPEC = 'symphonyVoiceCallWeb';

/**
 * The out-of-credits "earn credits instead" offer. Exported so the flag's own module reads the same
 * literal — and the NATIVE apps conduct this exact spec id, so the three surfaces ramp together.
 */
export const EARN_CREDITS_OFFER_SPEC = 'symphonyOutOfCreditsEarnOfferEnabled';

/**
 * The package picker's billing-cycle-step arm.
 *
 * **Deliberately NOT `…Web` like its three siblings above.** This is the key Petri actually holds
 * (`fetchExperimentById 5209969`), and `conduct` matches the string exactly — so the panel has to
 * name the same one the server asks for. It read `symphonyBillingCycleStepWeb` while Petri held
 * `specs.orion.SymphonyBillingCycleStep`, which is what left an allowlist of three named users on
 * the old picker with the experiment looking correctly configured.
 *
 * The `orion` in it is a live identifier, not prose, so the rename does not touch it (CLAUDE.md).
 *
 * It genuinely is the only surface that conducts this — but NOT because the phones have a picker of
 * their own. iOS and Android both send buyers to this exact page (`CreditsUpgrade.symphonyURL` ->
 * symphony.wix.com/packages), so all three surfaces are already served by this one gate.
 */
export const BILLING_CYCLE_STEP_SPEC = 'specs.orion.SymphonyBillingCycleStep';

/**
 * The DRAWER credits card's upsell arm (Figma 15375-105152 mobile / 15375-105248 rail): the card drops
 * its usage sentence and progress bar for a one-line upsell subtitle, and its CTA becomes a full-width
 * button at the foot. Control is the card as it ships — plan name + CTA on one row, the usage line and
 * meter below.
 *
 * ⚠️ **This string must be BYTE-IDENTICAL to the key Petri holds** — the route matches it exactly and
 * nothing else (see there). The id was chosen by hand rather than derived from a convention, so verify
 * it against the created experiment rather than trusting this comment: the picker's arm above asked for
 * `symphonyBillingCycleStepWeb` while Petri held `specs.orion.SymphonyBillingCycleStep`, and that gap
 * cost a whole ramp. A Petri experiment created *from a spec* generally materialises with a
 * `specs.<scope>.<Name>` key; if that is how this one is created, this literal has to change with it.
 *
 * **Web-only on purpose:** the phones conduct their own `symphonyHideDrawerProgressBarNative` for the
 * same change, so the two surfaces can be ramped and read independently (asked for explicitly). Do NOT
 * collapse them into one id without deciding that a single arm should move web and both phones at once.
 */
export const HIDE_DRAWER_PROGRESS_BAR_SPEC = 'symphonyHideDrawerProgressBarWeb';

/**
 * The daily-limit bar's rollout gate — the second meter reading how much of today's allowance is
 * spent (Figma 15375-104311).
 *
 * **This is the CAP's own key, not a web arm of its own**, so the bar rolls out to exactly the
 * population the ceiling applies to rather than ramping out of step with it. Hence bare camelCase
 * with no `…Web` suffix and no `specs.` prefix — do not "correct" it to either shape.
 *
 * ⚠️ Which object answers this key has been read wrong once — see the route for the evidence and for
 * why "there are two objects" is NOT the settled conclusion it was first written as. Survivable either
 * way, because the bar also requires the BFF to have sent the numbers.
 *
 * Forcing this On in the panel does NOT conjure a bar on an account the BFF reports no ceiling for —
 * the data is the other half of the gate, and it is the half that cannot be overridden.
 */
export const DAILY_LIMIT_BAR_SPEC = 'symphonyDailyCreditsLimit';

/**
 * Every toggle this app conducts. Add one ONLY when something actually reads it — same rule as
 * native's `ExperimentSpecs.startup`: a spec nobody reads is noise in that experiment's analysis.
 */
export const WEB_TOGGLES: WebToggle[] = [
  {
    spec: INSTANCE_AUTH_SPEC,
    description: 'Signed-instance auth for the data layer (off = the legacy credentialed fetch).',
  },
  {
    spec: VOICE_CALL_SPEC,
    description: 'Live voice calls. Voice is ON by default; set this release to control to turn it off.',
  },
  {
    spec: EARN_CREDITS_OFFER_SPEC,
    description:
      'The "Earn free credits" offer at the out-of-credits moments (rail credits card, paused routine, buy-credits). Off until conducted.',
  },
  {
    spec: BILLING_CYCLE_STEP_SPEC,
    description:
      'Package picker A/B: On = no Monthly/Yearly toggle, the cycle is chosen on a second screen after the plan. Off = today’s one-screen picker.',
  },
  {
    spec: HIDE_DRAWER_PROGRESS_BAR_SPEC,
    description:
      'Drawer/rail credits card A/B: On = no usage line or meter, an upsell subtitle instead, and a full-width CTA at the foot. Off = today’s card. Drawer only — the account cards are untouched.',
  },
  {
    spec: DAILY_LIMIT_BAR_SPEC,
    description:
      'The daily-limit bar on the credits surfaces (today’s allowance, beside the period meter). Shows nothing unless the account is actually under a per-day ceiling — the numbers come from the server and cannot be forced.',
  },
];

const PREFIX = 'orion-web:dev-ft:';
const key = (spec: string): string => `${PREFIX}${spec}`;

function store(persist: boolean): Storage | undefined {
  try {
    if (typeof window === 'undefined') return undefined;
    return persist ? window.localStorage : window.sessionStorage;
  } catch {
    return undefined; // storage blocked — no overrides, the conducted value stands
  }
}

function read(persist: boolean, spec: string): ToggleOverride | undefined {
  try {
    const value = store(persist)?.getItem(key(spec));
    return value === 'true' || value === 'false' ? value : undefined;
  } catch {
    return undefined;
  }
}

/**
 * The forced value for a spec, or `undefined` for "use the conducted value". Persisted overrides win
 * over session ones (a Persist=on write clears the session copy, so both are never set at once).
 *
 * Safe to call from anywhere, including the flag read-path: without a dev-tools grant it is always
 * `undefined`, so this cannot change what a real user's app does.
 */
export function toggleOverride(spec: string): ToggleOverride | undefined {
  if (!devToolsUnlocked()) return undefined;
  return read(true, spec) ?? read(false, spec);
}

/** Whether this spec's override is flagged to survive a reload (native: `isOverridePersisted`). */
export function isOverridePersisted(spec: string): boolean {
  if (!devToolsUnlocked()) return false;
  return read(true, spec) !== undefined;
}

/**
 * Set or clear an override (native: `setDevOverride`).
 *
 * - `value` `null` → back to Default: dropped from BOTH stores.
 * - otherwise → written to the store `persist` selects, and removed from the other, so flipping Persist
 *   moves the override rather than leaving a stale twin behind.
 */
export function setToggleOverride(spec: string, value: ToggleOverride | null, persist: boolean): void {
  if (!devToolsUnlocked()) return;
  try {
    if (value === null) {
      store(true)?.removeItem(key(spec));
      store(false)?.removeItem(key(spec));
      return;
    }
    store(persist)?.setItem(key(spec), value);
    store(!persist)?.removeItem(key(spec));
  } catch {
    /* quota / blocked storage — the panel simply shows no override */
  }
}

/** Drop every override (the panel's "Use conducted values"). */
export function clearToggleOverrides(): void {
  if (!devToolsUnlocked()) return;
  for (const { spec } of WEB_TOGGLES) setToggleOverride(spec, null, true);
}

/** How many toggles are currently forced — what the dev menu badges itself with. */
export function overriddenToggleCount(): number {
  return WEB_TOGGLES.filter(({ spec }) => toggleOverride(spec) !== undefined).length;
}
